import React, { useState, useEffect, useCallback } from 'react';
import { insightsApi } from '../api/client';

// A window on the quote history. Read-only.
//
// What is visible here is exactly what the price engine will get as training
// material. That is why it also shows the fields you would normally hide:
// whether a cost is the planned or the actual one, and whether a line was
// noted as an estimate. Whoever distrusts the model must be able to check the
// input.

const formatMoney = (amount) =>
  amount === null || amount === undefined
    ? ''
    : `€ ${Number(amount).toLocaleString('nl-BE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

function QuoteWindow() {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [bookedOnly, setBookedOnly] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    insightsApi
      .packages()
      .then((r) => setPackages(r.data))
      .catch(() => setError('De pakketten konden niet opgehaald worden.'));
  }, []);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: 100 };
      if (selectedPackage) params.tour_id = Number(selectedPackage);
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (bookedOnly) params.booked_only = true;
      const r = await insightsApi.quotes(params);
      setQuotes(r.data);
    } catch (e) {
      setError('De offertes konden niet opgehaald worden.');
    } finally {
      setLoading(false);
    }
  }, [selectedPackage, dateFrom, dateTo, bookedOnly]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const toggleDetail = async (id) => {
    if (detail && detail.id === id) {
      setDetail(null);
      return;
    }
    try {
      const r = await insightsApi.quote(id);
      setDetail(r.data);
    } catch (e) {
      setError('Die offerte kon niet opgehaald worden.');
    }
  };

  return (
    <div>
      <h2>Offertes</h2>
      <p className="intro">
        De historiek waar de prijsengine op leert. Proefblokken uit de import
        staan er nooit bij. De marge staat op basis exclusief btw, want dat is
        de basis waarop de vloeren gelden.
      </p>

      {error && <div className="message error">{error}</div>}

      <div className="filter-bar">
        <label>
          Pakket
          <select value={selectedPackage} onChange={(e) => setSelectedPackage(e.target.value)}>
            <option value="">alle pakketten</option>
            {packages.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.quotes})
              </option>
            ))}
          </select>
        </label>
        <label>
          Vertrek vanaf
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label>
          tot
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={bookedOnly}
            onChange={(e) => setBookedOnly(e.target.checked)}
          />
          alleen geboekte
        </label>
      </div>

      {loading && <p>Bezig met ophalen.</p>}

      {!loading && quotes.length === 0 && (
        <p>Geen offertes met deze filters.</p>
      )}

      {quotes.length > 0 && (
        <table className="insights-table">
          <thead>
            <tr>
              <th>Pakket</th>
              <th>Vertrek</th>
              <th className="number">Pers.</th>
              <th className="number">Verkoop</th>
              <th className="number">Nettokost</th>
              <th className="number">Marge</th>
              <th className="number">Marge %</th>
              <th>Geboekt</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {quotes.map((q) => (
              <React.Fragment key={q.id}>
                <tr
                  className={detail && detail.id === q.id ? 'selected' : ''}
                  onClick={() => toggleDetail(q.id)}
                >
                  <td>{q.package || <span className="empty">geen pakket</span>}</td>
                  <td>{formatDate(q.departure_date)}</td>
                  <td className="number">{q.persons}</td>
                  <td className="number">{formatMoney(q.sale_price)}</td>
                  <td className="number">{formatMoney(q.net_cost)}</td>
                  <td className="number">{formatMoney(q.margin)}</td>
                  <td className="number">
                    {q.margin_percent === null ? '' : `${q.margin_percent} %`}
                  </td>
                  <td>
                    {q.booked === true && <span className="badge yes">geboekt</span>}
                    {q.booked === false && <span className="badge no">niet</span>}
                    {q.booked === null && <span className="empty">onbeslist</span>}
                  </td>
                  <td className="expander">{detail && detail.id === q.id ? '−' : '+'}</td>
                </tr>
                {detail && detail.id === q.id && (
                  <tr className="detail-row">
                    <td colSpan={9}>
                      <QuoteDetail quote={detail} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function QuoteDetail({ quote }) {
  return (
    <div className="quote-detail">
      <div className="detail-header">
        {quote.source_tab && <span>tab: {quote.source_tab}</span>}
        {quote.option_label && <span>variant: {quote.option_label}</span>}
        {quote.observed_on && <span>opgezocht op {formatDate(quote.observed_on)}</span>}
        {quote.return_date && <span>terug {formatDate(quote.return_date)}</span>}
        {quote.reason && <span>reden: {quote.reason}</span>}
      </div>

      <table className="components">
        <thead>
          <tr>
            <th>Soort</th>
            <th>Omschrijving</th>
            <th>Plaats</th>
            <th>Datum</th>
            <th className="number">Nachten</th>
            <th className="number">Kost</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {quote.components.map((c) => (
            <tr key={c.id}>
              <td>{c.component_type === 'accommodation' ? 'hotel' : c.component_type}</td>
              <td>{c.description}</td>
              <td>{c.place}</td>
              <td>{formatDate(c.travel_date)}</td>
              <td className="number">{c.nights}</td>
              <td className="number">{formatMoney(c.cost)}</td>
              <td className="badges">
                {/* These two decide whether a line counts as an observation. */}
                {c.estimated && <span className="badge warning">schatting</span>}
                {c.cost_is_actual && <span className="badge yes">werkelijk</span>}
                {c.tax_at_property && (
                  <span className="badge">taks {formatMoney(c.tax_at_property)}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {quote.notes && <p className="notes">{quote.notes}</p>}
    </div>
  );
}

export default QuoteWindow;
